import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import BadgeStatus from "../../../common/BadgeStatus";
import AlertService from "../../../components/SweetAlert";
import ToastUtil from "../../../components/ToastNotification/ToastUtil";
import { newFetch, PUT } from "../../../helpers/fetch";
import "./styles.css";

const ClaimsLog = (props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [selectedClaims, setSelectedClaims] = useState([]);

  const handleCheckboxChange = (id, isChecked) => {
    if (isChecked) {
      setSelectedClaims((prev) => [...prev, id]);
    } else {
      setSelectedClaims((prev) => prev.filter((claimId) => claimId !== id));
    }
  };

  const handleBulkCancel = async () => {
    if (selectedClaims.length === 0) return;

    if (
      await AlertService.confirm(
        "",
        `${t("Are you sure you want to cancel")} ${selectedClaims.length} ${t(
          "claim"
        )}?`
      )
    ) {
      const response = await newFetch(PUT, "/associate/claims/bulk-cancel", {
        claimIds: selectedClaims,
      });

      if (response.success) {
        ToastUtil.success(t("Claim(s) cancelled successfully"));
        setSelectedClaims([]);
        props.updateState();
      } else {
        ToastUtil.error(response.message || `${t("Failed to cancel claims")}`);
      }
    }
  };

  // Helper function to check if claim is pending
  const isPending = (status) => {
    return status.toLowerCase().includes("pending");
  };

  return (
    <section className="claims-submissions" aria-labelledby="claims-submissions-title">
      <div className="claims-submissions__header">
        <div>
          <span className="claims-submissions__eyebrow">Claim history</span>
          <h5 id="claims-submissions-title">{t("Submissions")}</h5>
          <p>{props.claims.length} {props.claims.length === 1 ? "record" : "records"}</p>
        </div>
        <button
          type="button"
          className="btn claims-submissions__cancel"
          disabled={selectedClaims.length === 0}
          onClick={handleBulkCancel}
        >
          <i className="fas fa-times" aria-hidden="true" />
          {t("Cancel Claim")}
          {selectedClaims.length > 0 && <span>{selectedClaims.length}</span>}
        </button>
      </div>

      <div className="claims-submissions__list">
        {props.claims.length > 0 ? (
          props.claims.map((claim) => {
            const receiptDate = claim.date_of_receipt
              ? (() => {
                  const [day, month, year] = claim.date_of_receipt.split("/");
                  const date = new Date(`${year}-${month}-${day}`);
                  return isNaN(date)
                    ? "-"
                    : date
                        .toLocaleDateString("en-GB", {
                          day: "2-digit",
                          month: "short",
                          year: "numeric",
                        })
                        .replace(/ /, ", ");
                })()
              : "-";

            const claimTitle =
              claim.claim_type_name === "Staff Purchase"
                ? t("Staff Purchase")
                : claim.claim_type_name === "Transportation"
                ? t("Transportation")
                : claim.claim_type_name;

            return (
              <article key={claim.id} className="claim-submission-card">
                <div className="claim-submission-card__top">
                  {isPending(claim.status) && (
                    <label className="claim-submission-card__select">
                      <input
                        type="checkbox"
                        value={claim.id}
                        checked={selectedClaims.includes(claim.id)}
                        onChange={(e) =>
                          handleCheckboxChange(claim.id, e.target.checked)
                        }
                        aria-label={`${t("Select claim")} ${claim.id}`}
                      />
                      <span aria-hidden="true" />
                    </label>
                  )}
                  <div className="claim-submission-card__identity">
                    <small>Claim #{claim.id}</small>
                  <button
                    type="button"
                    className="claim-submission-card__title"
                    onClick={() =>
                      history.push({
                        pathname: "/claimsupdate",
                        state: { claim },
                      })
                    }
                  >
                    {claimTitle}
                  </button>
                  </div>
                  <BadgeStatus status={claim.status} />
                </div>

                <dl className="claim-submission-card__details">
                  <div>
                    <dt>{t("Receipt date")}</dt>
                    <dd>{receiptDate}</dd>
                  </div>
                  <div>
                    <dt>{t("Amount")}</dt>
                    <dd>
                      {claim.amount != null ? claim.amount.toFixed(2) : "0.00"} SGD
                    </dd>
                  </div>
                    {props.activeSite?.enable_claim_percentage && (
                    <div>
                      <dt>{t("Deducted amount")}</dt>
                      <dd className="claim-submission-card__deduction">
                        {claim.deduct_amount != null
                          ? claim.deduct_amount.toFixed(2)
                          : "0.00"}{" "}
                        SGD
                      </dd>
                    </div>
                    )}
                </dl>

                <button
                  type="button"
                  className="claim-submission-card__open"
                  onClick={() =>
                    history.push({ pathname: "/claimsupdate", state: { claim } })
                  }
                  aria-label={`${t("View claim")} ${claim.id}`}
                >
                  {t("View details")}
                  <i className="fas fa-arrow-right" aria-hidden="true" />
                </button>
              </article>
            );
          })
        ) : (
          <div className="claims-submissions__empty">
            <span aria-hidden="true"><i className="fas fa-receipt" /></span>
            <strong>{t("No records found")}</strong>
            <p>Your submitted claims will appear here.</p>
          </div>
        )}
      </div>
    </section>
  );
};

export default ClaimsLog;
